import { Injectable } from '@angular/core';
import { ShipConfig } from '../models/ship-config.model';
import { Carriers, UPSPickUpType } from '../../shared/enum/general-enum';
import { User } from '../../shared/services/user/models/user.model';
import { Customer } from '../../shared/models/customer/customer.model';
import moment from 'moment';
import { Location } from '../../shared/models/location/location.model';
import { EUCountries } from '../../shared/models/countries-eu.model';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import SafeUrlAssembler from 'safe-url-assembler';
import { HttpClientService } from '../../core/services/http/http-client.service';
import { ShipUserConfig } from '../models/ship-user-config.model';
import { ShipmentService } from './shipment.service';
import { ApiEndpointService } from '../../core/services/api-endpoint.service';
import { ICountry } from '../../shared/models/country.interface';

@Injectable()
export class ShipConfigService {
    public Config: ShipConfig = new ShipConfig();
    public configSubject = new BehaviorSubject<ShipConfig>(null);
    public configSetCount = 0;
    public isDefaultUserConfigApplied = false;

    private getUserConfigSubscription: Subscription;

    private storageName = 'shipConfigData';
    private euCountryCodes = EUCountries.map(country => country.alpha2Code);

    constructor(private httpClientService: HttpClientService,
                private apiEndpointService: ApiEndpointService,
                private shipmentService: ShipmentService) {
    }

    public getUserConfig(carrierCode: string): Observable<ShipUserConfig> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('getUserCarrierConfiguration'))
            .param('carrierCode', carrierCode)
            .toString();

        return this.httpClientService.get<ShipUserConfig>(endpoint);
    }

    public clearConfig() {
        this.Config = new ShipConfig();
        this.configSubject.next(null);
        this.isDefaultUserConfigApplied = false;
        this.configSetCount = 0;
    }

    public loadUserConfig(carrierCode: string, user: User) {
        const userConfigMap = {
            isThermalLabel: 'AlwaysUseThermal',
            isPrintHighValue: 'AlwaysPrintHighValue',
            isNotifySender: 'AlwaysNotifySender',
            isNotifyRecipient: 'AlwaysNotifyRecipient',
            isSendCustomEmailToRecipient: 'AlwaysSendCustomEmailToRecipient',
        };

        if (this.getUserConfigSubscription) {
            this.getUserConfigSubscription.unsubscribe();
            this.getUserConfigSubscription = null;
        }

        this.getUserConfigSubscription = this.getUserConfig(carrierCode)
            .subscribe(
                (value) => {
                    if (!value || !Object.keys(value).length) {
                        return;
                    }

                    // The user config always overwrites the default behaviors.
                    const userConfig = new ShipUserConfig();
                    const userConfigKeys = Object.getOwnPropertyNames(userConfig);
                    userConfigKeys.forEach(
                        (key) => {
                            // If the config doesn't contain the expected property, take it from the user object if it exists.
                            if (!Object.prototype.hasOwnProperty.call(value, key)
                                && Object.prototype.hasOwnProperty.call(userConfigMap, key)) {
                                userConfig[key] = user[userConfigMap[key]];
                            } else {
                                userConfig[key] = value[key];
                            }
                        },
                    );

                    // always load show* configs, but not initial configs.
                    if (!this.isDefaultUserConfigApplied) {
                        const oneTimeConfigs = this.getOneTimeUserConfigProperties(userConfig);
                        oneTimeConfigs.forEach(
                            (key) => {
                                this.Config[key] = userConfig[key];
                            },
                        );

                        this.isDefaultUserConfigApplied = true;
                    }

                    const priorityConfigs = this.getPriorityUserConfigProperties(userConfig);
                    priorityConfigs.forEach(
                        (key) => {
                            this.Config[key] = userConfig[key];
                        },
                    );

                    this.Config.isAPIDriven = true;
                    this.configSubject.next(this.Config);
                },
            );
    }

    private getOneTimeUserConfigProperties(userConfig: ShipUserConfig) {
        const userConfigKeys = Object.getOwnPropertyNames(userConfig);
        return userConfigKeys.filter(key => !key.startsWith('show'));
    }

    private getPriorityUserConfigProperties(userConfig: ShipUserConfig) {
        const userConfigKeys = Object.getOwnPropertyNames(userConfig);
        return userConfigKeys.filter(key => key.startsWith('show'));
    }

    public restoreConfig(carrier: Carriers,
                         user: User,
                         customer: Customer,
                         shipFromLocation: Location) {
        const storedConfig = sessionStorage.getItem(this.storageName);

        if (storedConfig) {
            this.Config = JSON.parse(storedConfig);
            return;
        }

        // this.Config = new ShipConfig();
        this.setDefaultConfig(carrier, user, customer, shipFromLocation);
    }

    public setDefaultConfig(carrier: Carriers,
                            user: User,
                            customer: Customer,
                            shipFromLocation: Location) {
        // TODO: these user- and customer-based properties may override changed value upon the config subject changed.
        // They are supposed to be run only once on the start.
        // Consider extracting them to a one-time call function when the shipment page is loaded
        // or make this function a one-time call function (independent to the config subject).
        this.Config.showBillToThirdParty = customer.AllowBillToThirdParty;
        this.Config.isThermalLabel = user.AlwaysUseThermal;
        this.Config.showNotifyRecipient = user.AlwaysNotifyRecipient;
        this.Config.isNotifyRecipient = user.AlwaysNotifyRecipient;
        this.Config.isNotifySender = user.AlwaysNotifySender;

        this.Config.isCheckResidential = shipFromLocation.IsResidential;

        if (user.IsGuest) {
            this.Config.showCheckResidential = false;
            this.Config.showSaveInAddressBook = false;
            this.Config.showAddToExpressContacts = false;
        }

        const isDomestic = this.isDomestic(shipFromLocation.Country, user.DefaultShipToCountry);
        const isEuToEu = this.isEuToEu(shipFromLocation.Country, user.DefaultShipToCountry);
        const isFromUS = shipFromLocation.Country.trim().toUpperCase() === 'US';
        const isToUS = user.DefaultShipToCountry.trim().toUpperCase() === 'US';
        const isUSInternational = (isFromUS && !isToUS) || (!isFromUS && isToUS);
        const isUSDomestic = isDomestic && isToUS;
        const isFromEU = this.euCountryCodes.includes(shipFromLocation.Country.trim().toUpperCase());
        const isToEU = this.euCountryCodes.includes(user.DefaultShipToCountry.trim().toUpperCase());
        const isEUDomestic = isDomestic && isToEU;
        const isEUInternational = (isFromEU && !isToEU) || (!isFromEU && isToEU);
        const isUPS = this.isUps(carrier);
        const isFedEx = this.isFedEx(carrier);
        const isUPSDomestic = isUPS && isDomestic;
        const isUPSInternational = isUPS && !isDomestic;
        const isFedExDomestic = isFedEx && isDomestic;
        const isFedExInternational = isFedEx && !isDomestic;
        const shipmentDate = moment();

        if (isDomestic) {
            this.Config.showSaturdayDelivery = true;
            this.Config.showBillToAccountSelector = user.IsCustomBillTo;
        }

        if (!isDomestic || !isEuToEu) {
            this.Config.showBrokerSelect = true;
            this.Config.showBillDutiesTaxes = true;
            this.Config.showExportInformation = !isDomestic;
            this.Config.showEEI = !isDomestic && isFromUS && !this.isDEGBUser(user);

            this.Config.showNAFTA = this.isNAFTA(shipFromLocation.Country, user.DefaultShipToCountry);
            this.Config.showCommodities = true;
        }

        if (isFromUS) {
            this.Config.showSaturdayPickUp = shipmentDate.day() === 6; // saturday
        }

        if (isUSInternational) {
            this.Config.showBrokerSelect = true;
        }

        if (isUSDomestic) {
            this.Config.showCod = customer.AllowCOD;
            this.Config.showReturnLabel = true;
            this.Config.showAdultSignature = true;
        }

        if (isEUDomestic) {
            this.Config.showAdultSignature = true;
            this.Config.showDirectSignature = true;
            this.Config.isDirectSignature = true;

            this.Config.showUseAlreadyScheduledPickUp = true;
            this.Config.showSchedulePickUp = true;
            this.Config.showThermalPrinter = true;
            this.Config.showReturnLabel = true;
        }

        if (isEUInternational) {
            this.Config.showBrokerSelect = true;
            this.Config.showAdultSignature = true;
            this.Config.showDirectSignature = true;
            this.Config.isDirectSignature = true;

            this.Config.showUseAlreadyScheduledPickUp = true;
            this.Config.showSchedulePickUp = true;
            this.Config.showThermalPrinter = true;
            this.Config.isThermalLabel = user.AlwaysUseThermal;
        }

        if (isUPS) {
            this.Config.pickupUPSType = shipFromLocation.UPSPickUpType;
            this.Config.showUPSBillTo = true;
        }

        if (isFedEx) {
            this.Config.showHoldAtFedexLocation = true;
            this.Config.showAdultSignatureMessage = shipFromLocation.Country !== 'CA';
            this.Config.showDirectSignature = true;
        }

        if (isUPS && (isEUDomestic || isEUInternational)) {
            this.Config.showAdultSignature = true;
            this.Config.showDirectSignature = true;
            this.Config.isDirectSignature = true;
        }

        if (isUPSDomestic || isUSDomestic) {
            this.Config.showSaturdayPickUp = shipmentDate.day() === 6;
            this.Config.showSaturdayDelivery = true;
            this.Config.showAdultSignature = true;
            this.Config.showCod = customer.AllowCOD;

            this.Config.showUseAlreadyScheduledPickUp = true;
            this.Config.showSchedulePickUp = true;
            this.Config.showThermalPrinter = true;
            this.Config.isThermalLabel = user.AlwaysUseThermal;
            this.Config.showReturnLabel = true;
        }

        if (isFedExDomestic || isUSDomestic) {
            this.Config.showSaturdayPickUp = shipmentDate.day() === 6;
            this.Config.showSaturdayDelivery = true;
            this.Config.showAdultSignature = true;
            /*this.Config.showDirectSignature = true;  [MV3-3315] visible to UPS too. already we are making these properties true in if(isFedEx) block.
            this.Config.showHoldAtFedexLocation = true; */
            this.Config.showCod = customer.AllowCOD;

            this.Config.showUseAlreadyScheduledPickUp = true;
            this.Config.showSchedulePickUp = true;
            this.Config.showThermalPrinter = true;
            this.Config.isThermalLabel = user.AlwaysUseThermal;
            this.Config.showReturnLabel = true;
        }

        if (isUPSInternational || isUSInternational) {
            this.Config.showUseAlreadyScheduledPickUp = true;
            this.Config.showSchedulePickUp = true;
            this.Config.showThermalPrinter = true;
            this.Config.isThermalLabel = user.AlwaysUseThermal;
        }

        if (isFedExInternational) {
            this.Config.showHoldAtFedexLocation = true;
            this.Config.showBrokerSelect = true;

            this.Config.showUseAlreadyScheduledPickUp = true;
            this.Config.showSchedulePickUp = true;
            this.Config.showThermalPrinter = true;
            this.Config.isThermalLabel = user.AlwaysUseThermal;
        }

        if (isFedEx && !isDomestic && user.DefaultShipToCountry.trim().toUpperCase() === 'US') {
            this.Config.showAdultSignature = true;
            this.Config.showDirectSignature = true;
        }
        this.Config.showDropOffUPS = isUPS && !isFromEU;
        this.Config.showDropOffFedEx = isFedEx && !isFromEU;

        // if (isEUToNonEU) {
        //   this.Config.showMRN = true;
        // }

        this.configSubject.next(this.Config);
    }

    public setConfig(shipFromCountryCode: string,
                     shipToCountryCode: string,
                     carrier: Carriers,
                     user: User,
                     customer: Customer,
                     insuredValue: number,
                     totalCustomsValue: number,
                     shipDateString: string,
                     upsPickupType?: UPSPickUpType) {
        if (!shipFromCountryCode ||
            !shipToCountryCode ||
            !carrier ||
            !user ||
            !customer) {
            return;
        }

        const isDomestic = this.isDomestic(shipFromCountryCode, shipToCountryCode);
        const isFromUS = shipFromCountryCode.trim().toUpperCase() === 'US';
        const isToUS = shipToCountryCode.trim().toUpperCase() === 'US';
        const isFromCA = shipFromCountryCode.trim().toUpperCase() === 'CA';
        const isToCA = shipToCountryCode.trim().toUpperCase() === 'CA';
        const isFromUK = shipFromCountryCode.trim().toUpperCase() === 'GB';
        const isFromPR = shipFromCountryCode.trim().toUpperCase() === 'PR';
        const isInternational = shipFromCountryCode !== shipToCountryCode;
        const isUSInternational = (isFromUS && !isToUS) || (!isFromUS && isToUS);
        const isUSDomestic = isFromUS && isToUS;
        const isCADomestic = isFromCA && isToCA;
        const isFromEU = this.euCountryCodes.includes(shipFromCountryCode.trim().toUpperCase());
        const isToEU = this.euCountryCodes.includes(shipToCountryCode.trim().toUpperCase());
        const isEUDomestic = isDomestic && isToEU;
        const isEUInternational = (isFromEU && !isToEU) || (!isFromEU && isToEU);
        const isUPS = this.isUps(carrier);
        const isFedEx = this.isFedEx(carrier);
        const isUPSDomestic = isUPS && isDomestic;
        const isFedExDomestic = isFedEx && isDomestic;
        const isFedExInternationalToUS = isFedEx && !isDomestic && isToUS;
        const isUpsEuDomestic = isUPS && isEUDomestic;
        const isUpsEuInternational = isUPS && isEUInternational;
        const isUPSInternational = isUPS && !isDomestic;
        const isFedExInternational = isFedEx && !isDomestic;
        const isEUToNonEU = isFromEU && !isToEU;

        // [MV3-1671] Remove VI from the EEI-exception countries.
        const shipToEEIExceptionCountries = ['CA', 'MX', 'GU'];
        if (isUPS) {
            shipToEEIExceptionCountries.push('PR');
        }

        const isShipToEEIExceptionCountry = shipToEEIExceptionCountries.includes(shipToCountryCode.trim().toUpperCase()) && isFromUS;

        // Ship To
        // [MV3-1959] Ignore user.AlwaysNotifyRecipient for showing/hiding notify-recipient options.
        //FI-504 - Reverted showNotifyRecipient changes to the previous logic.
        this.Config.showNotifyRecipient = +insuredValue === 0 && (!isFromEU && !isFromUK);
        // if(!this.isEUUser(user)) {
        //     this.Config.showNotifyRecipient = +insuredValue === 0 && !isFromEU;
        // }
        // Billing Details
        this.Config.showBillDutiesTaxes = !isDomestic;
        this.Config.showUPSBillTo = isUPS;

        // Additional Shipment Information
        // if (isDomestic) {
        this.Config.showBrokerSelect = !isDomestic || isUSInternational || isEUInternational || isFedExInternational;
        const isSaturdayShipping = moment(shipDateString).isValid() && moment(shipDateString).day() === 6;
        const isFedExCADomestic = isCADomestic && isFedEx;
        this.Config.showSaturdayPickUp = (isDomestic || isFromUS || isFedExCADomestic) && isSaturdayShipping;
        this.Config.showSaturdayDelivery = isToUS || isFedExCADomestic;
        this.Config.showCod = (isUSDomestic || isFedExCADomestic) && customer.AllowCOD;

        this.Config.showDirectDeliveryOnly = isUPS; //;
        this.Config.isDirectDeliveryOnly = this.Config.showDirectDeliveryOnly && (isToEU || isFromEU);

        const isSignatureForbidden = isFedEx && !isToUS;
        this.Config.showAdultSignature = (isToEU || !isSignatureForbidden) && !(isFedEx && shipFromCountryCode === 'CA' && !isCADomestic);

        // [MV3-2076] Remove isFedEx from the show-adult-signature-message conditions.
        this.Config.showAdultSignatureMessage = this.Config.showAdultSignature;

        const isFedExDirectSignatureAllowed = (isFedEx && isToUS) || (isFedEx && isFromCA && isToCA) || (isFedEx && isFromUS && isToCA);
        const isUPSDirectSignatureAllowed = isUPS && this.isDEGBUser(user);
        this.Config.showDirectSignature = isToEU || isFedExDirectSignatureAllowed || isUPSDirectSignatureAllowed;
        this.Config.isDirectSignature = isUPSDirectSignatureAllowed;

        this.Config.showHoldAtFedexLocation = (isFedEx && isToUS) || (isFedEx && isFromCA);
        // } else {
        //   // [MV3-1781] Change conditions for showing/hiding options for DE/GB users.
        //   this.Config.showBrokerSelect = !isDomestic;
        //   const isSaturdayShipping = moment(shipDateString).isValid() && moment(shipDateString).day() === 6;
        //   this.Config.showSaturdayDelivery = isToUS && this.isDEGBUser(user);
        //   this.Config.showSaturdayPickUp = (isDomestic || isFromUS) && isSaturdayShipping && this.isDEGBUser(user);
        //
        //   const isSignatureForbidden = isFedEx && !isToUS;
        //   this.Config.showAdultSignature = (isToEU || !isSignatureForbidden) && this.isDEGBUser(user);
        //   this.Config.showAdultSignatureMessage = this.Config.showAdultSignature && isFedEx;
        //
        //   const isFedExToUS = isFedEx && isToUS;
        //   this.Config.showDirectSignature = (isToEU || isFedExToUS || isUPS) && this.isDEGBUser(user);
        //   this.Config.isDirectSignature = isUPS && this.isDEGBUser(user);
        //
        //   const isShippedToUSByDEGB = isToUS && this.isDEGBUser(user);
        //   const isShippedInternationalToUSByNonDEGB = isToUS && !isDomestic && !this.isDEGBUser(user);
        //   const isShippedWithinCA = isFromCA && isToCA;
        //   this.Config.showHoldAtFedexLocation = isFedEx && (isShippedToUSByDEGB || isShippedInternationalToUSByNonDEGB || isShippedWithinCA);
        //   this.Config.showCod = customer.AllowCOD && isUSDomestic && this.isDEGBUser(user);
        // }

        this.Config.showAdditionalShipmentInfo =
            this.Config.showSaturdayPickUp ||
            this.Config.showSaturdayDelivery ||
            this.Config.showBrokerSelect ||
            this.Config.showCod ||
            this.Config.showAdultSignature ||
            this.Config.showDirectSignature ||
            this.Config.showHoldAtFedexLocation;

        // Shipping Options
        if (isUPS && upsPickupType) {
            this.Config.pickupUPSType = upsPickupType;
        }

        this.Config.showDropOffUPS = isUPS && !isFromEU;  // && this.isDEGBUser(user);
        this.Config.showDropOffFedEx = isFedEx && !isFromEU; // && this.isDEGBUser(user);

        this.Config.showSchedulePickUp =
            isUPS ||
            isFedEx ||
            isUSDomestic ||
            isUSInternational ||
            isEUDomestic ||
            isEUInternational;
        this.Config.showUseAlreadyScheduledPickUp =
            isUPS ||
            isFedEx ||
            isUSDomestic ||
            isUSInternational ||
            isEUDomestic ||
            isEUInternational;
        this.Config.showThermalPrinter =
            isUPS ||
            isFedEx ||
            isUSDomestic ||
            isUSInternational ||
            isEUDomestic ||
            isEUInternational;
        this.Config.showReturnLabel = isDomestic;

        // Export Information
        this.Config.showExportInformation = !isDomestic;
        this.Config.showEEI = !isDomestic && !this.isDEGBUser(user);

        const isHighValueCommodityFromUS =
            // [MV3-1752] Remove no-longer valid conditions.
            // isFromUS &&
            // !isShipToEEIExceptionCountry &&
            this.shipmentService.hasHighValueCommodity() &&
            (
                // [MV3-1751] Exclude PR from NAFTA for UPS.
                // [MV3-1752] Remove PR from exception.
                // (isUPS && !this.isNAFTA(shipFromCountryCode, shipToCountryCode, ['PR'])) ||

                // [MV3-1714] Exclude PR from NAFTA for FedEx.
                // [MV3-1752] Remove PR from exception.
                // (isFedEx && !this.isNAFTA(shipFromCountryCode, shipToCountryCode, ['PR'])) ||

                // [MV3-1752] Added a list of ship-from/to countries that EEI should be exempted.
                !this.shouldEEIBeExempted(shipFromCountryCode, shipToCountryCode)
            );

        // [MV3-1751] Added an EEI requirement condition for PR to US.
        const isHighValueCommodityFromPRToUS = isFromPR && isToUS && this.shipmentService.hasHighValueCommodity();
        this.Config.requireEEI = this.showEEIComplete(shipFromCountryCode, shipToCountryCode);

        // [MV3-1692] As agreed (not mentioned in the ticket), the following conditions should be enough.
        // [MV3-1697] The MRN is only for German customers.
        this.Config.showMRN = isEUToNonEU &&
            ((isFromUK && totalCustomsValue >= 800) || totalCustomsValue >= 1000) &&
            isUPS &&
            this.isDEUser(user);

        this.Config.showPowerOfAttorney = this.Config.showEEI && user.ShowAESAgreement;

        // [MV3-1592] Remove the NAFTA restriction.
        // [MV3-2785] adjusted logic to check config.highValueCommodity to check if it should override country logic
        const b13PreCondition = isFromCA && isInternational; // && this.isNAFTA(shipFromCountryCode, shipToCountryCode);
        const isB13Exception = isFromCA && ['CA', 'GU', 'PR', 'US', 'VI'].includes(shipToCountryCode.trim().toUpperCase());
        this.Config.showB13 = (b13PreCondition && !isB13Exception && totalCustomsValue > 1999) ||
            (b13PreCondition && this.Config.highValueCommodity === true);
        this.Config.showNoB13Required = (b13PreCondition && totalCustomsValue <= 1999) ||
            (b13PreCondition && this.Config.highValueCommodity === false);

        // Customs Documentation
        this.Config.showNAFTA = this.isNAFTA(shipFromCountryCode, shipToCountryCode);

        // Commodity
        if(this.isEuToEu(shipFromCountryCode, shipToCountryCode)) {
            this.Config.showCommodities = false;
        }
        else{
            if(isDomestic){
                this.Config.showCommodities = false;
            }
            else{
                this.Config.showCommodities = true;
            }
        }
        
        this.configSubject.next(this.Config);
        this.configSetCount++;
    }

    /**
     * Checks if the shipment is NAFTA (North American Free Trade Agreement).
     * That is the origin or destination country falls into a certain country list.
     *
     * @param {string} shipFromCountryCode
     * @param {string} shipToCountryCode
     * @param excludedCountryCodes
     * @returns {any}
     */
    public isNAFTA(shipFromCountryCode: string, shipToCountryCode: string, excludedCountryCodes: string[] = []) {
        if (!shipFromCountryCode || !shipToCountryCode || shipFromCountryCode === shipToCountryCode) {
            return false;
        }

        // [MV3-1714] Allow certain countries to be excluded from NAFTA.
        const naftaCountries = ['CA', 'MX', 'PR', 'US'].filter(item => !excludedCountryCodes.includes(item));
        const isShipFromNafta = naftaCountries.includes(shipFromCountryCode.trim().toUpperCase());
        const isShipToNafta = naftaCountries.includes(shipToCountryCode.trim().toUpperCase());

        return isShipFromNafta && isShipToNafta;
    }

    // New EEI Rules 2/15/2018 -csinde
    // [MV3-2785] adjusted logic to check config.highValueCommodity to check if it should override country logic
    public showEEIComplete(shipFromCountryCode: string, shipToCountryCode: string): boolean {

        if (shipFromCountryCode === shipToCountryCode) {
            return false;
        }

        if (!this.shipmentService.hasHighValueCommodity()) {
            return false;
        }
        if (shipFromCountryCode === 'GU') {
            if (shipToCountryCode === 'CA' || shipToCountryCode === 'GU' || shipToCountryCode === 'US') {
                return false;
            }

            return true;
        }
        if (shipFromCountryCode === 'PR' || shipFromCountryCode === 'VI') {
            if (shipToCountryCode === 'CA' || shipToCountryCode === 'GU') {
                return false;
            }

            return true;
        }
        if (shipFromCountryCode === 'US') {
            if ((shipToCountryCode === 'CA' || shipToCountryCode === 'GU') && this.Config.highValueCommodity === false) {
                return false;
            }

            return true;
        }

        return false;
    };

    public shouldEEIBeExempted(shipFromCountryCode: string, shipToCountryCode: string): boolean {
        if (!shipFromCountryCode || !shipToCountryCode || shipFromCountryCode === shipToCountryCode) {
            return true;
        }

        if (!this.shipmentService.hasHighValueCommodity()) {
            return true;
        }

        switch (true) {
            case (shipFromCountryCode === 'US' && shipToCountryCode === 'CA'):
            case (shipFromCountryCode === 'CA' && shipToCountryCode === 'US'):
            case (shipFromCountryCode === 'US' && shipToCountryCode === 'GU'):
            case (shipFromCountryCode === 'GU' && shipToCountryCode === 'US'):

                // [MV3-1791] US <-> VI requires EEI.
                // case (shipFromCountryCode === 'US' && shipToCountryCode === 'VI'):
                // case (shipFromCountryCode === 'VI' && shipToCountryCode === 'US'):
                return true;
            default:
                return false;
        }
    }

    public setShowFedExBillTo(carrier: Carriers, accountNumber: string, isShowRates: boolean) {
        if (!this.isFedEx(carrier)) {
            this.Config.showFedExBillTo = false;
            return;
        }

        this.Config.showFedExBillTo = !accountNumber && isShowRates;

        this.configSubject.next(this.Config);
    }

    public setUPSPickupType(carrier: Carriers, shipFromLocation: Location) {
        if (!this.isUps(carrier) || !shipFromLocation) {
            return;
        }

        this.Config.pickupUPSType = shipFromLocation.UPSPickUpType;

        this.configSubject.next(this.Config);
    }

    public setPostalCodeAware(country: ICountry) {
        if (!country) return;

        this.Config.isShipToPostalCodeAware = country.IsPostalCodeAware;

        this.configSubject.next(this.Config);
    }

    // public setRequireEEI(shipFromCountryCode: string, shipToCountryCode: string) {
    //   if (shipFromCountryCode === 'US' && (shipToCountryCode === 'CN' || shipToCountryCode === 'VE' || shipToCountryCode === 'RU')) {
    //     this.Config.requireEEI = true;
    //     this.configSubject.next(this.Config);
    //   }
    // }

    /**
     * Checks if the shipment is a domestic shipment.
     *
     * @param {string} shipFromCountryCode
     * @param {string} shipToCountryCode
     * @returns {boolean}
     */
    public isDomestic(shipFromCountryCode: string, shipToCountryCode: string) {
        if (!shipFromCountryCode || !shipToCountryCode) {
            return false;
        }

        return shipFromCountryCode.trim().toUpperCase() === shipToCountryCode.trim().toUpperCase();
    }

    /**
     * Checks if the provided carrier is UPS.
     *
     * @param {Carriers} carrier
     * @returns {boolean}
     */
    public isUps(carrier: Carriers) {
        return +Carriers[carrier] === Carriers.Ups;
    }

    /**
     * Checks if the provided carrier is FedEx.
     *
     * @param {Carriers} carrier
     * @returns {boolean}
     */
    public isFedEx(carrier: Carriers) {
        return +Carriers[carrier] === Carriers.Fedex;
    }

    public isDomesticOnlylUser(user: User) {
        return (user.IsUPSDomestic && !user.IsUPSInternational)
            || (user.IsFedExDomestic || !user.IsFedExInternational);
    }

    public isDEGBUser(user: User) {
        // return user.UserName.startsWith('DE') || user.UserName.startsWith('GB');
        return ['DE', 'GB'].includes(user.CountryCode?.toUpperCase());
    }

    public isDEUser(user: User) {
        // return user.UserName.startsWith('DE');
        return ['DE'].includes(user.CountryCode?.toUpperCase());
    }

    public isGBUser(user: User) {
        // return user.UserName.startsWith('GB');
        return ['GB'].includes(user.CountryCode?.toUpperCase());
    }

    public isEUUser(user: User) {
        return this.euCountryCodes.includes(user.CountryCode?.toUpperCase());
    }

    public isFromUS(shipFromCountry: string): boolean {
        return shipFromCountry.trim().toUpperCase() === 'US';
    }

    public isToUS(shipToCountry: string): boolean {
        return shipToCountry.trim().toUpperCase() === 'US';
    }

    /*
     * From US to Non-US or Non-US to US.
     */
    public isUSInternational(shipFromCountry: string, shipToCountry: string): boolean {
        return this.isFromUS(shipFromCountry) && !this.isToUS(shipToCountry) ||
            !this.isFromUS(shipFromCountry) && this.isToUS(shipToCountry);
    }

    /*
     * Within US.
     */
    public isUSDomestic(shipFromCountry: string, shipToCountry: string): boolean {
        return this.isDomestic(shipFromCountry, shipToCountry) && this.isToUS(shipToCountry);
    }

    public isFromEU(shipFromCountry: string): boolean {
        return this.euCountryCodes.includes(shipFromCountry?.trim().toUpperCase());
    }

    public isToEU(shipToCountry: string): boolean {
        return this.euCountryCodes.includes(shipToCountry?.trim().toUpperCase());
    }

    /*
     * Within the SAME EU country.
     */
    public isEUDomestic(shipFromCountry: string, shipToCountry: string): boolean {
        return this.isDomestic(shipFromCountry, shipToCountry) && (this.isToEU(shipToCountry) || shipToCountry == 'GB');
    }

    /*
     * From EU to Non-EU or Non-EU to EU.
     */
    public isEUInternational(shipFromCountry: string, shipToCountry: string): boolean {
        return this.isEuToNonEu(shipFromCountry, shipToCountry) &&
            this.isNonEuToEu(shipFromCountry, shipToCountry);
    }

    /*
     * Within EU, but NOT the same country.
     */
    public isInterEU(shipFromCountry: string, shipToCountry: string): boolean {
        return (this.isFromEU(shipFromCountry) || shipFromCountry == 'GB') &&
            (this.isToEU(shipToCountry) || shipToCountry == 'GB') &&
            !this.isDomestic(shipFromCountry, shipToCountry);
    }

    /*
     * Within the SAME country with UPS.
     */
    public isUPSDomestic(carrier: Carriers, shipFromCountry: string, shipToCountry: string): boolean {
        return this.isUps(carrier) && this.isDomestic(shipFromCountry, shipToCountry);
    }

    /*
     * From/to different countries with UPS.
     */
    public isUPSInternational(carrier: Carriers, shipFromCountry: string, shipToCountry: string): boolean {
        return this.isUps(carrier) && !this.isDomestic(shipFromCountry, shipToCountry);
    }

    /*
     * Within the SAME country with FedEx.
     */
    public isFedExDomestic(carrier: Carriers, shipFromCountry: string, shipToCountry: string): boolean {
        return this.isFedEx(carrier) && this.isDomestic(shipFromCountry, shipToCountry);
    }

    /*
     * From/to different countries with FedEx.
     */
    public isFedExInternational(carrier: Carriers, shipFromCountry: string, shipToCountry: string): boolean {
        return this.isFedEx(carrier) && !this.isDomestic(shipFromCountry, shipToCountry);
    }

    /*
     * Within ANY EU countries; domestic or international.
     */
    public isEuToEu(shipFromCountry: string, shipToCountry: string): boolean {
        return this.isFromEU(shipFromCountry) && this.isToEU(shipToCountry);
    }

    /*
     * From Non-EU country to EU country.
     */
    public isNonEuToEu(shipFromCountry: string, shipToCountry: string): boolean {
        return !this.isFromEU(shipFromCountry) && (this.isToEU(shipToCountry) || shipToCountry == 'GB');
    }

    /*
     * From EU country to Non-EU country.
          */
    public isEuToNonEu(shipFromCountry: string, shipToCountry: string): boolean {
        return (this.isFromEU(shipFromCountry) || shipFromCountry == 'GB') && !this.isToEU(shipToCountry);
    }

    /*
     * From Non-EU to Non-EU country.
     */
    public isNonEuToNonEu(shipFromCountry: string, shipToCountry: string): boolean {
        return !this.isFromEU(shipFromCountry) && !this.isToEU(shipToCountry) && (shipFromCountry != 'GB' && shipToCountry != 'GB');
    }
}
